import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  type ApiOrgContext,
  apiCall,
  fetchWithTimeout,
  apiErrorFor,
  getPathScope,
  normalizeOrgContext,
  resolveOrgSelector,
} from './core';

/**
 * Server-side page-size ceiling shared by every paginated agent tool
 * (`MAX_TOOL_PAGE_SIZE` in `@lobu/core/contracts/tools/pagination`). Asking for
 * more is a 400 that the query layer reports as an empty result, so a caller
 * that wants "everything on one page" must clamp to this instead of picking a
 * larger number.
 */
export const MAX_TOOL_PAGE_SIZE = 500;

type NormalizedCtx = { organizationId: string | null; slug: string | null };
type OrgContextParam = ApiOrgContext | string | null | undefined;

/**
 * Each `createOrgQuery` / `createQuery` returns a `useXxx` hook that also
 * carries a `.queryOptions(...)` builder. Pass the same args you'd pass to the
 * hook; the builder returns a plain TanStack Query options object that route
 * loaders can hand to `queryClient.ensureQueryData(...)` for prefetching.
 *
 * This means the hook stays the only definition of (queryKey, queryFn,
 * staleTime) — the loader can't drift out of sync with the component.
 */
type QueryOptionsBuilder<TArgs extends readonly unknown[], TResult> = (
  ...args: TArgs
) => {
  queryKey: unknown[];
  queryFn: () => Promise<TResult>;
  staleTime?: number;
};

type QueryHookWithOptions<TArgs extends readonly unknown[], TResult> = ((
  ...args: TArgs
) => ReturnType<typeof useQuery<TResult>>) & {
  queryOptions: QueryOptionsBuilder<TArgs, TResult>;
};

// ---------------------------------------------------------------------------
// Query factory for hooks that call apiCall with org-context resolution.
//
// The caller-facing argument tuple is TArgs (which includes the orgContext
// parameter at whatever position the original hook had it). The config
// receives the full TArgs tuple and is responsible for pulling out
// orgContext and building the body / queryKey.
// ---------------------------------------------------------------------------

export function createOrgQuery<TArgs extends readonly unknown[], TResult>(config: {
  queryKey: (ctx: NormalizedCtx, ...args: TArgs) => unknown[];
  tool: string;
  body: (...args: TArgs) => Record<string, unknown>;
  orgContext: (...args: TArgs) => OrgContextParam;
  transform?: (data: any) => TResult;
  enabled?: (ctx: NormalizedCtx, ...args: TArgs) => boolean;
  placeholderData?: (ctx: NormalizedCtx, ...args: TArgs) => TResult | undefined;
  staleTime?: number;
}): QueryHookWithOptions<TArgs, TResult> {
  const buildBaseOptions = (...args: TArgs) => {
    const ctx = normalizeOrgContext(config.orgContext(...args));
    return {
      ctx,
      queryKey: config.queryKey(ctx, ...args),
      queryFn: async (): Promise<TResult> => {
        const result = await apiCall<any>(
          config.tool,
          config.body(...args),
          resolveOrgSelector(ctx)
        );
        return config.transform ? config.transform(result) : (result as TResult);
      },
      staleTime: config.staleTime,
    };
  };

  const hook = (...args: TArgs) => {
    const base = buildBaseOptions(...args);
    const hasContext = !!(base.ctx.organizationId || base.ctx.slug);
    return useQuery({
      queryKey: base.queryKey,
      queryFn: base.queryFn,
      enabled: config.enabled?.(base.ctx, ...args) ?? hasContext,
      placeholderData: config.placeholderData?.(base.ctx, ...args) as never,
      staleTime: base.staleTime,
    });
  };

  (hook as QueryHookWithOptions<TArgs, TResult>).queryOptions = (...args: TArgs) => {
    const base = buildBaseOptions(...args);
    return { queryKey: base.queryKey, queryFn: base.queryFn, staleTime: base.staleTime };
  };

  return hook as QueryHookWithOptions<TArgs, TResult>;
}

// ---------------------------------------------------------------------------
// Query factory for hooks that call apiCall WITHOUT org-context
// ---------------------------------------------------------------------------

export function createQuery<TArgs extends readonly unknown[], TResult>(config: {
  queryKey: (...args: TArgs) => unknown[];
  tool: string;
  body: (...args: TArgs) => Record<string, unknown>;
  transform?: (data: any) => TResult;
  enabled?: (...args: TArgs) => boolean;
  staleTime?: number;
}): QueryHookWithOptions<TArgs, TResult> {
  const buildBaseOptions = (...args: TArgs) => ({
    queryKey: config.queryKey(...args),
    queryFn: async (): Promise<TResult> => {
      const result = await apiCall<any>(config.tool, config.body(...args));
      return config.transform ? config.transform(result) : (result as TResult);
    },
    staleTime: config.staleTime,
  });

  const hook = (...args: TArgs) => {
    const base = buildBaseOptions(...args);
    return useQuery({
      queryKey: base.queryKey,
      queryFn: base.queryFn,
      enabled: config.enabled?.(...args) ?? true,
      staleTime: base.staleTime,
    });
  };

  (hook as QueryHookWithOptions<TArgs, TResult>).queryOptions = buildBaseOptions;

  return hook as QueryHookWithOptions<TArgs, TResult>;
}

// ---------------------------------------------------------------------------
// Mutation factory
// ---------------------------------------------------------------------------

export function createMutation<TParams, TResult = unknown>(config: {
  tool: string;
  body: (params: TParams) => Record<string, unknown>;
  invalidateKeys: string[];
  transform?: (data: any) => TResult;
  checkError?: boolean; // default true -- throw if result.error exists
  // Static text, or a function of the mutation result for outcome-dependent
  // toasts (return null to suppress -- e.g. a create that queued an approval
  // instead of creating).
  successMessage?: string | ((result: TResult) => string | null);
  // Fallback toast text when a thrown error has no message. Defaults to the
  // generic 'Something went wrong'.
  errorMessage?: string;
  // Also invalidate on ERROR, not just success. Set for actions whose failure
  // usually means the UI is stale (e.g. approving a run another tab/replica
  // already resolved — "Run not found or not pending"): refetching then removes
  // the stale control that produced the error instead of leaving it clickable.
  invalidateOnError?: boolean;
}) {
  return (orgContext?: ApiOrgContext | string) => {
    const queryClient = useQueryClient();
    const invalidateAll = () => {
      for (const key of config.invalidateKeys) {
        queryClient.invalidateQueries({ queryKey: [key] });
      }
    };
    return useMutation({
      mutationFn: async (params: TParams) => {
        const result = await apiCall<any>(config.tool, config.body(params), orgContext);
        if (config.checkError !== false && result.error) {
          throw new Error(result.error);
        }
        return config.transform ? config.transform(result) : (result as TResult);
      },
      onSuccess: (result: TResult) => {
        invalidateAll();
        const message =
          typeof config.successMessage === 'function'
            ? config.successMessage(result)
            : config.successMessage;
        if (message) {
          toast.success(message);
        }
      },
      onError: (error: Error) => {
        if (config.invalidateOnError) invalidateAll();
        toast.error(error.message || config.errorMessage || 'Something went wrong');
      },
    });
  };
}

// ---------------------------------------------------------------------------
// REST mutation factory — for hooks that hit REST verbs (POST/PATCH/DELETE) on
// path-scoped endpoints via fetchWithTimeout + throwIfNotOk, rather than the
// tool-style `apiCall`. The returned hook takes the same hook-level args the
// original did (e.g. an agentId); `url` and `invalidate` receive the resolved
// slug plus those args. `body` is omitted for no-body verbs (DELETE). `read`
// controls response parsing: 'json' (default), 'none' (void), or a custom
// reader for non-standard envelopes.
// ---------------------------------------------------------------------------

export function createRestMutation<
  THookArgs extends readonly unknown[],
  TParams = void,
  TResult = unknown,
>(config: {
  method: 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  // `url` gets the resolved slug, the mutate params (some endpoints put an id
  // from params in the path), and the hook-level args (e.g. agentId). `invalidate`
  // gets slug + args. Object form so call sites destructure only what they use.
  url: (ctx: { slug: string; params: TParams; args: THookArgs }) => string;
  body?: (params: TParams) => unknown;
  read?: 'json' | 'none' | ((res: Response) => Promise<TResult>);
  invalidate: (ctx: { slug: string | null; args: THookArgs }) => unknown[][];
  successMessage?: string;
  errorMessage: string;
}) {
  return (...hookArgs: THookArgs) => {
    const { slug } = getPathScope();
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: async (params: TParams): Promise<TResult> => {
        const init: RequestInit = { method: config.method, credentials: 'include' };
        if (config.body) {
          init.headers = { 'Content-Type': 'application/json' };
          init.body = JSON.stringify(config.body(params));
        }
        const res = await fetchWithTimeout(
          config.url({ slug: slug as string, params, args: hookArgs }),
          init
        );
        if (!res.ok) {
          throw await apiErrorFor(res);
        }
        const read = config.read ?? 'json';
        if (read === 'none') return undefined as TResult;
        if (read === 'json') return (await res.json()) as TResult;
        return read(res);
      },
      onSuccess: () => {
        for (const key of config.invalidate({ slug, args: hookArgs })) {
          queryClient.invalidateQueries({ queryKey: key });
        }
        if (config.successMessage) {
          toast.success(config.successMessage);
        }
      },
      onError: (error: Error) => {
        toast.error(error.message || config.errorMessage);
      },
    });
  };
}
